import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { projectsData } from "@/data/projects";
import { transformProject } from "@/lib/projectTransformer";

// Module-level cache — survives component unmount/remount (React Router navigation).
// Stale after 3 minutes so a re-published project shows up without a full page reload.
let _cache     = null;
let _promise   = null;
let _cacheTime = 0;
const TTL      = 3 * 60 * 1000; // 3 minutes

const fetchProjects = () => {
  if (!supabase) return Promise.resolve(null);

  const now = Date.now();
  if (_cache && now - _cacheTime < TTL) return Promise.resolve(_cache);

  // Reset promise on TTL expiry so we actually re-fetch
  if (now - _cacheTime >= TTL) _promise = null;

  if (_promise) return _promise;

  _promise = supabase
    .from("projects")
    .select("*")
    .eq("is_published", true)
    .order("sort_order", { ascending: true })
    .then(({ data, error }) => {
      if (error || !data || data.length === 0) return null;
      const result = data.map(transformProject);
      _cache     = result;
      _cacheTime = Date.now();
      return result;
    })
    .catch(() => null);

  return _promise;
};

export function useProjects() {
  const [projects, setProjects] = useState(() => _cache ?? projectsData);
  const [loading,  setLoading]  = useState(!_cache);

  useEffect(() => {
    if (_cache && Date.now() - _cacheTime < TTL) {
      setProjects(_cache);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchProjects().then(result => {
      if (result) setProjects(result);
      setLoading(false);
    });
  }, []);

  return { projects, loading };
}
